/**
 * Estimates — create proposals/quotes that can be converted to Invoices.
 *
 * Status pipeline:  draft → sent → accepted → converted (→ invoice)
 *                              ↘ rejected
 *
 * Storage: localStorage `maidly_estimates`
 * Convert: appends a new invoice to `maidly_estimates_invoices_target` and
 *          flips the estimate to status='converted' with `convertedInvoiceId`.
 *
 * Reuses InvoiceFormModal (same form shape: customer, items, tax, discount,
 * notes) plus an extra `validUntil` date field.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Edit, Trash2, Send, FileText, Check, X as XIcon, Eye, Download, ArrowRight, Mail } from 'lucide-react';
import { toast } from 'sonner';
import {
  formatInvoiceDate as formatDate,
  calculateInvoiceTotals,
  CLEANING_SERVICES,
  getNextDocumentNumber,
  isDocumentNumberTaken,
} from '../utils/invoiceHelpers';
import InvoiceFormModal from '../components/invoices/InvoiceFormModal';
import InvoicePreviewModal from '../components/invoices/InvoicePreviewModal';
import DocumentEmailModal from '../components/invoices/DocumentEmailModal';
import { generateInvoicePDF } from '../utils/PDFGenerator';
import { IconTile, TINT } from '../components/ui/IconTile';
import { ActionTile } from '../components/ui/ActionTile';

const STORAGE_KEY = 'maidly_estimates';
const INVOICES_KEY = 'maidly_invoices';

const STATUS_META = {
  draft:     { label: 'Draft',     bg: '#f1f5f9', fg: '#475569' },
  sent:      { label: 'Sent',      bg: '#dbeafe', fg: '#1d4ed8' },
  accepted:  { label: 'Accepted',  bg: '#dcfce7', fg: '#166534' },
  rejected:  { label: 'Rejected',  bg: '#fee2e2', fg: '#991b1b' },
  converted: { label: 'Converted', bg: '#fef3c7', fg: '#92400e' },
};

const blankForm = (defaultTaxRate = 8) => ({
  customerId: '',
  customerName: '',
  customerContactName: '',
  customerAddress: '',
  customerEmail: '',
  customerPhone: '',
  estimateNumber: '',
  poNumber: '',
  date: new Date().toISOString().split('T')[0],
  items: [{ id: crypto.randomUUID(), description: '', quantity: 1, rate: 0, amount: 0 }],
  subtotal: 0,
  tax: 0,
  taxRate: defaultTaxRate,
  discount: 0,
  total: 0,
  status: 'draft',
  dueDate: '',   // = validUntil for estimates; reused so InvoiceFormModal works as-is
  notes: '',
  terms: 'This estimate is valid for 30 days.',
  footerNote: '',
  depositAmount: 0,
  recurring: false,
  recurringFrequency: 'monthly',
});

const Estimates = () => {
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultTaxRate, setDefaultTaxRate] = useState(8);
  const [formData, setFormData] = useState(() => blankForm(8));
  const [filter, setFilter] = useState('all');
  const [previewEstimate, setPreviewEstimate] = useState(null);
  const [emailEstimate, setEmailEstimate] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({});
  const previewRef = useRef(null);

  useEffect(() => {
    setEstimates(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
    setCustomers(JSON.parse(localStorage.getItem('maidly_customers') || '[]'));
    const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
    if (settings.companyInfo) setCompanyInfo(settings.companyInfo);
    const tr = Number(settings.taxRate ?? settings.salesTaxRate);
    if (Number.isFinite(tr)) {
      setDefaultTaxRate(tr);
      setFormData((f) => ({ ...f, taxRate: tr }));
    }
  }, []);

  // Re-load when another part of the app (e.g. the Cleaning Calculator's
  // "Save as Estimate") writes new rows. Same pattern Invoices.js uses.
  useEffect(() => {
    const refresh = () => {
      setEstimates(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
      // ALSO reload customers so an address added in the Customers tab (or
      // an invoice created in the Invoices tab) shows up immediately here
      // without needing a full page reload.
      setCustomers(JSON.parse(localStorage.getItem('maidly_customers') || '[]'));
    };
    window.addEventListener('maidly:estimates-updated', refresh);
    window.addEventListener('maidly:customers-updated', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('maidly:estimates-updated', refresh);
      window.removeEventListener('maidly:customers-updated', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  // Cross-tab tax rate sync — refresh defaultTaxRate + current form when
  // the user saves Settings elsewhere.
  useEffect(() => {
    const refresh = () => {
      try {
        const s = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
        const tr = Number(s.taxRate ?? s.salesTaxRate);
        if (Number.isFinite(tr)) {
          setDefaultTaxRate(tr);
          setFormData((f) => ({ ...f, taxRate: tr }));
        }
      } catch (_) { /* ignore */ }
    };
    window.addEventListener('maidly:settings-updated', refresh);
    return () => window.removeEventListener('maidly:settings-updated', refresh);
  }, []);

  const persist = (next, meta = {}) => {
    setEstimates(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    try {
      window.dispatchEvent(new CustomEvent('maidly:estimates-updated', {
        detail: { source: 'estimates-tab', ...meta },
      }));
    } catch (_) { /* ignore */ }
  };

  const counts = useMemo(() => {
    const out = { all: 0, draft: 0, sent: 0, accepted: 0, rejected: 0, converted: 0 };
    estimates.forEach((e) => { out[e.status] = (out[e.status] || 0) + 1; });
    // "All" count excludes converted to match the default filter view.
    out.all = estimates.length - (out.converted || 0);
    return out;
  }, [estimates]);

  const filtered = useMemo(
    () => (filter === 'all'
            // The default "All" view hides converted estimates — they've moved
            // on to the Invoices tab and don't need to clutter the work-list.
            // The dedicated "Converted (N)" filter pill still surfaces them.
            ? estimates.filter((e) => e.status !== 'converted')
            : estimates.filter((e) => e.status === filter))
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [estimates, filter]
  );

  // Reused by InvoiceFormModal --------------------------------------------------
  const handleCustomerChange = (id) => {
    const c = customers.find((x) => x.id === id);
    const contactName = c
      ? [c.contactFirstName, c.contactLastName].filter(Boolean).join(' ').trim()
      : '';
    // Also pull the customer's contact info onto the estimate so BILL TO
    // renders correctly on the preview / PDF and the "No mailing address"
    // warning doesn't false-fire when the customer actually HAS an address.
    setFormData({
      ...formData,
      customerId: id,
      customerName: c?.name || '',
      customerContactName: contactName,
      customerAddress: c?.address || '',
      customerEmail: c?.email || '',
      customerPhone: c?.phone || '',
    });
  };

  const handleItemChange = (idx, field, value) => {
    const items = [...formData.items];
    items[idx] = { ...items[idx], [field]: value };
    if (field === 'quantity' || field === 'rate') {
      const qty = parseFloat(items[idx].quantity) || 0;
      const rate = parseFloat(items[idx].rate) || 0;
      items[idx].amount = qty * rate;
    }
    const totals = calculateInvoiceTotals(items, formData.taxRate, formData.discount, formData.depositAmount);
    setFormData({ ...formData, items, ...totals });
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { id: crypto.randomUUID(), description: '', quantity: 1, rate: 0, amount: 0 }],
    });
  };

  const handleAddCleaningService = (svc) => {
    const item = { id: crypto.randomUUID(), description: svc.description, quantity: 1, rate: svc.rate, amount: svc.rate };
    const items = [...formData.items.filter((i) => i.description), item];
    const totals = calculateInvoiceTotals(items, formData.taxRate, formData.discount, formData.depositAmount);
    setFormData({ ...formData, items, ...totals });
  };

  const handleRemoveItem = (idx) => {
    if (formData.items.length === 1) return;
    const items = formData.items.filter((_, i) => i !== idx);
    const totals = calculateInvoiceTotals(items, formData.taxRate, formData.discount, formData.depositAmount);
    setFormData({ ...formData, items, ...totals });
  };

  const calculateTotals = (taxRate, discount) => {
    const totals = calculateInvoiceTotals(formData.items, taxRate, discount, formData.depositAmount);
    setFormData({ ...formData, taxRate, discount, ...totals });
  };

  // CRUD ------------------------------------------------------------------------
  const openCreate = () => {
    setEditing(null);
    setFormData(blankForm(defaultTaxRate));
    setShowModal(true);
  };

  const openEdit = (est) => {
    setEditing(est);
    setFormData({ ...blankForm(defaultTaxRate), ...est });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.customerId) {
      toast.error('Pick a customer');
      return;
    }
    const now = new Date().toISOString();
    if (editing) {
      const next = estimates.map((x) =>
        x.id === editing.id ? { ...x, ...formData, updatedAt: now } : x
      );
      persist(next, { action: 'update', estimateId: editing.id });
      toast.success('Estimate updated');
    } else {
      // Collision-proof numbering (was estimates.length+1, which reused a
      // number after any deletion). Honor a typed number only if it's free.
      const typed = (formData.estimateNumber || '').trim();
      const estimateNumber = (typed && !isDocumentNumberTaken(estimates, typed))
        ? typed
        : getNextDocumentNumber(estimates, 'EST');
      const estimate = {
        ...formData,
        id: crypto.randomUUID(),
        estimateNumber,
        createdAt: now,
      };
      persist([estimate, ...estimates], { action: 'create', estimateId: estimate.id });
      toast.success('Estimate created');
    }
    setShowModal(false);
    setEditing(null);
  };

  const setStatus = (id, status) => {
    persist(
      estimates.map((e) => (e.id === id ? { ...e, status, updatedAt: new Date().toISOString() } : e)),
      { action: 'status', estimateId: id, status },
    );
    toast.success(`Marked ${status}`);
  };

  const remove = (id) => {
    if (!window.confirm('Delete this estimate?')) return;
    persist(estimates.filter((e) => e.id !== id), { action: 'delete', estimateId: id });
    toast.success('Estimate deleted');
  };

  // Attach doc-type + latest customer contact info so the shared preview /
  // PDF renders BILL TO correctly even when the estimate was created before
  // the customer had an address on file.
  const enrichForPreview = (est) => {
    const cust = customers.find((c) => c.id === est.customerId) || {};
    return {
      ...est,
      _docType: 'estimate',
      customerAddress: est.customerAddress || cust.address || '',
      customerEmail:   est.customerEmail   || cust.email   || '',
      customerPhone:   est.customerPhone   || cust.phone   || '',
    };
  };

  const openPreview = (est) => setPreviewEstimate(enrichForPreview(est));
  const downloadPdf = (est) => {
    try {
      generateInvoicePDF(enrichForPreview(est), companyInfo);
    } catch (e) {
      console.warn('[estimates] PDF failed', e);
      toast.error('Failed to generate PDF');
    }
  };

  const convertToInvoice = (est) => {
    if (est.status === 'converted' && est.convertedInvoiceId) {
      toast.error('Already converted');
      return;
    }
    const invoices = JSON.parse(localStorage.getItem(INVOICES_KEY) || '[]');
    const now = new Date().toISOString();
    const newInvoice = {
      ...est,
      id: crypto.randomUUID(),
      // Use the same collision-proof INV-YYYY-NNN series as the Invoices page
      // so a converted estimate never reuses an existing invoice number.
      invoiceNumber: getNextDocumentNumber(invoices, 'INV'),
      status: 'draft',
      createdAt: now,
      updatedAt: now,
      fromEstimateId: est.id,
      fromEstimateNumber: est.estimateNumber,
    };
    delete newInvoice.estimateNumber;
    localStorage.setItem(INVOICES_KEY, JSON.stringify([newInvoice, ...invoices]));
    // Tell the Invoices tab to reload from localStorage so the newly-created
    // invoice appears the moment the user switches tabs (without a refresh).
    window.dispatchEvent(new CustomEvent('maidly:invoices-updated', { detail: { source: 'estimate-convert', invoiceId: newInvoice.id } }));
    persist(
      estimates.map((e) =>
        e.id === est.id
          ? { ...e, status: 'converted', convertedInvoiceId: newInvoice.id, updatedAt: now }
          : e
      ),
      { action: 'convert', estimateId: est.id, invoiceId: newInvoice.id },
    );
    toast.success(`Converted to ${newInvoice.invoiceNumber}`);
  };

  const statusPill = (status) => {
    const meta = STATUS_META[status] || STATUS_META.draft;
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 999,
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
        background: meta.bg, color: meta.fg,
      }}>{meta.label}</span>
    );
  };

  const filterPills = [
    { id: 'all',       label: `All (${counts.all})` },
    { id: 'draft',     label: `Draft (${counts.draft || 0})` },
    { id: 'sent',      label: `Sent (${counts.sent || 0})` },
    { id: 'accepted',  label: `Accepted (${counts.accepted || 0})` },
    { id: 'rejected',  label: `Rejected (${counts.rejected || 0})` },
    { id: 'converted', label: `Converted (${counts.converted || 0})` },
  ];

  return (
    <div data-testid="estimates-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 10 }}>
          <IconTile icon={FileText} tint={TINT.recurring} size={44} />
          Estimates
        </h2>
        <button
          onClick={openCreate}
          data-testid="add-estimate-button"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            padding: '0.55rem 1rem 0.55rem 0.55rem',
            background: '#0073ea', color: '#fff', border: 'none', borderRadius: 12,
            fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 6px 16px rgba(0,115,234,0.35)',
            transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            letterSpacing: 0.1,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#0060c4';
            e.currentTarget.style.boxShadow = '0 10px 22px rgba(0,115,234,0.55)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = '#0073ea';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,115,234,0.35)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <IconTile icon={Plus} tint="rgba(255,255,255,0.22)" size={30} />
          New Estimate
        </button>
      </div>

      {/* Status filter pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '1rem' }}>
        {filterPills.map((p) => {
          const active = filter === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setFilter(p.id)}
              data-testid={`estimates-filter-${p.id}`}
              style={{
                background: active ? '#0073ea' : '#fff',
                color: active ? '#fff' : '#475569',
                border: `1px solid ${active ? '#0073ea' : '#e2e8f0'}`,
                borderRadius: 999, padding: '9px 18px',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.1,
                boxShadow: active ? '0 3px 10px rgba(0,115,234,0.28)' : 'none',
                transition: 'background 0.15s, color 0.15s, box-shadow 0.15s, border-color 0.15s',
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(15,23,42,0.05)'; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = '#fff'; }}
            >{p.label}</button>
          );
        })}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr style={{ textAlign: 'left' }}>
                <th style={th()}>Status</th>
                <th style={th()}>Date</th>
                <th style={th()}>Number</th>
                <th style={th()}>Customer</th>
                <th style={th()}>Valid until</th>
                <th style={th()}>Amount</th>
                <th style={th(true)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                  {filter === 'all' ? 'No estimates yet. Click "New Estimate" to create one.' : `No ${filter} estimates`}
                </td></tr>
              ) : filtered.map((est) => (
                <tr key={est.id} data-testid={`estimate-row-${est.id}`} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={td()}>{statusPill(est.status)}</td>
                  <td style={td()}>{est.createdAt ? formatDate(est.createdAt) : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={td()}><span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{est.estimateNumber}</span></td>
                  <td style={td()}>{est.customerName}</td>
                  <td style={td()}>{est.dueDate ? formatDate(est.dueDate) : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={td()}>${Number(est.total || 0).toFixed(2)}</td>
                  <td style={{ ...td(true), whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      {est.status !== 'converted' && est.status !== 'rejected' && (
                        <ActionTile
                          onClick={() => convertToInvoice(est)}
                          title="Convert to invoice"
                          icon={ArrowRight}
                          tint={TINT.invoices}
                          testid={`estimate-convert-${est.id}`}
                        />
                      )}
                      {est.status === 'draft' && (
                        <ActionTile
                          onClick={() => setStatus(est.id, 'sent')}
                          title="Mark Sent"
                          icon={Send}
                          tint={TINT.dashboard}
                          testid={`estimate-send-${est.id}`}
                        />
                      )}
                      {est.status === 'sent' && (
                        <>
                          <ActionTile
                            onClick={() => setStatus(est.id, 'accepted')}
                            title="Mark Accepted"
                            icon={Check}
                            tint={TINT.green}
                            testid={`estimate-accept-${est.id}`}
                          />
                          <ActionTile
                            onClick={() => setStatus(est.id, 'rejected')}
                            title="Mark Rejected"
                            icon={XIcon}
                            tint={TINT.red}
                            testid={`estimate-reject-${est.id}`}
                          />
                        </>
                      )}
                      <ActionTile
                        onClick={() => openPreview(est)}
                        title="Preview estimate"
                        icon={Eye}
                        tint={TINT.dashboard}
                        testid={`estimate-preview-${est.id}`}
                      />
                      <ActionTile
                        onClick={() => setEmailEstimate(enrichForPreview(est))}
                        title="Email to customer"
                        icon={Mail}
                        tint={TINT.expenses}
                        testid={`estimate-email-${est.id}`}
                      />
                      <ActionTile
                        onClick={() => downloadPdf(est)}
                        title="Download PDF"
                        icon={Download}
                        tint={TINT.settings}
                        testid={`estimate-download-${est.id}`}
                      />
                      <ActionTile
                        onClick={() => openEdit(est)}
                        title="Edit estimate"
                        icon={Edit}
                        tint={TINT.timeClock}
                        disabled={est.status === 'converted'}
                        testid={`estimate-edit-${est.id}`}
                      />
                      <ActionTile
                        onClick={() => remove(est.id)}
                        title="Delete estimate"
                        icon={Trash2}
                        tint={TINT.expenses}
                        testid={`estimate-delete-${est.id}`}
                      />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reused InvoiceFormModal (Estimate flavor) */}
      <InvoiceFormModal
        open={showModal}
        editingInvoice={editing}
        entityLabel="Estimate"
        formData={formData}
        setFormData={setFormData}
        customers={customers}
        onClose={() => { setShowModal(false); setEditing(null); }}
        onSubmit={handleSubmit}
        onCustomerChange={handleCustomerChange}
        onItemChange={handleItemChange}
        onAddItem={handleAddItem}
        onAddCleaningService={handleAddCleaningService}
        onRemoveItem={handleRemoveItem}
        calculateTotals={calculateTotals}
      />

      {/* Shared preview modal — renders in Estimate mode via `_docType`. */}
      <InvoicePreviewModal
        open={!!previewEstimate}
        previewInvoice={previewEstimate}
        companyInfo={companyInfo}
        companySignature=""
        printRef={previewRef}
        onClose={() => setPreviewEstimate(null)}
        onDownload={downloadPdf}
        onRequestCustomerSignature={() => {}}
      />

      {/* Shared email modal (same visual as Invoices) — sends via the
          company's own Gmail SMTP configured in Settings, with the
          auto-generated Estimate PDF attached. */}
      <DocumentEmailModal
        open={!!emailEstimate}
        doc={emailEstimate}
        docKind="estimate"
        customer={emailEstimate ? customers.find((c) => c.id === emailEstimate.customerId) : null}
        companyInfo={companyInfo}
        onClose={() => setEmailEstimate(null)}
        onSent={() => {
          // Mark the estimate as "sent" when the user actually emails it.
          if (emailEstimate && emailEstimate.status === 'draft') {
            setStatus(emailEstimate.id, 'sent');
          }
        }}
      />
    </div>
  );
};

const th = (right) => ({
  padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: 0.4,
  textAlign: right ? 'right' : 'left',
});
const td = (right) => ({
  padding: '12px 14px', textAlign: right ? 'right' : 'left',
});

export default Estimates;
